/**
 * MultiCameraResults
 *
 * Design: Security Control Room
 * - Matrix layout for camera feeds
 * - Professional status monitoring indicators
 * - immersive "dark mode" video placeholders
 */
import React, { useState } from "react";
import { MultiCameraResults as MultiCameraResultsType } from "../../types";
import {
  Monitor,
  Video,
  VideoOff,
  WifiOff,
  Activity,
  Settings,
  Maximize2,
  Grid,
  AlertTriangle,
} from "lucide-react";

interface MultiCameraResultsProps {
  results: MultiCameraResultsType;
}

export const MultiCameraResults: React.FC<MultiCameraResultsProps> = ({
  results,
}) => {
  const [selectedCamera, setSelectedCamera] = useState<string | null>(null);

  // Get cameras to display (max 4 for current view)
  const displayCameras = results.cameras.slice(0, 4);
  const emptySlots = 4 - displayCameras.length;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return {
          text: "text-emerald-400",
          bg: "bg-emerald-500",
          border: "border-emerald-500/50",
        };
      case "inactive":
        return {
          text: "text-amber-400",
          bg: "bg-amber-500",
          border: "border-amber-500/50",
        };
      case "error":
        return {
          text: "text-red-400",
          bg: "bg-red-500",
          border: "border-red-500/50",
        };
      default:
        return {
          text: "text-slate-400",
          bg: "bg-slate-500",
          border: "border-slate-500/50",
        };
    }
  };

  const getSystemStatus = () => {
    if (results.active_cameras === results.total_cameras)
      return {
        label: "Fully Operational",
        color: "text-emerald-400",
        icon: <Activity className="w-5 h-5" />,
      };
    if (results.active_cameras === 0)
      return {
        label: "System Offline",
        color: "text-red-400",
        icon: <WifiOff className="w-5 h-5" />,
      };
    return {
      label: "Degraded Service",
      color: "text-amber-400",
      icon: <AlertTriangle className="w-5 h-5" />,
    };
  };

  const systemStatus = getSystemStatus();

  return (
    <div className="space-y-6">
      {/* System Status Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700/50 p-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-slate-800 rounded-xl border border-slate-700 shadow-xl">
              <Monitor className={`w-8 h-8 ${systemStatus.color}`} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white mb-1">
                Surveillance Grid
              </h2>
              <div
                className={`flex items-center gap-2 text-sm font-medium ${systemStatus.color}`}
              >
                {systemStatus.icon}
                {systemStatus.label}
              </div>
            </div>
          </div>

          <div className="flex items-center divide-x divide-slate-700/50 bg-slate-900/50 rounded-xl border border-slate-700/50">
            <div className="px-6 py-3 text-center">
              <p className="text-2xl font-bold text-emerald-400">
                {results.active_cameras}
              </p>
              <p className="text-xs text-slate-500 uppercase tracking-wider">
                Active
              </p>
            </div>
            <div className="px-6 py-3 text-center">
              <p className="text-2xl font-bold text-slate-400">
                {results.inactive_cameras}
              </p>
              <p className="text-xs text-slate-500 uppercase tracking-wider">
                Inactive
              </p>
            </div>
            <div className="px-6 py-3 text-center">
              <p className="text-2xl font-bold text-white">
                {results.total_cameras}
              </p>
              <p className="text-xs text-slate-500 uppercase tracking-wider">
                Total
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Camera Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {displayCameras.map((camera, index) => {
          const status = getStatusColor(camera.status);
          const isSelected = selectedCamera === camera.id;

          return (
            <div
              key={camera.id}
              onClick={() => setSelectedCamera(isSelected ? null : camera.id)}
              className={`
                group relative aspect-video bg-black rounded-xl overflow-hidden cursor-pointer border-2 transition-all
                ${
                  isSelected
                    ? "border-primary-500 ring-4 ring-primary-500/20 z-10 scale-[1.02]"
                    : "border-slate-800 hover:border-slate-600"
                }
              `}
            >
              {/* Header Overlay */}
              <div className="absolute top-0 inset-x-0 p-3 flex justify-between items-start z-20 bg-gradient-to-b from-black/80 to-transparent">
                <div className="flex items-center gap-2">
                  <span className="bg-slate-900/80 text-white text-xs font-mono px-2 py-1 rounded border border-slate-700">
                    CAM {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="text-xs font-medium text-white shadow-black drop-shadow-md">
                    {camera.location}
                  </span>
                </div>
                <div
                  className={`flex items-center gap-1.5 px-2 py-1 rounded bg-black/60 backdrop-blur-sm border ${status.border}`}
                >
                  <div
                    className={`w-2 h-2 rounded-full ${status.bg} ${
                      camera.status === "active" ? "animate-pulse" : ""
                    }`}
                  />
                  <span
                    className={`text-[10px] font-bold uppercase ${status.text}`}
                  >
                    {camera.status}
                  </span>
                </div>
              </div>

              {/* Video Content Placeholder */}
              <div className="absolute inset-0 flex items-center justify-center">
                {camera.status === "active" ? (
                  <>
                    <div className="absolute inset-0 bg-slate-900 flex items-center justify-center">
                      <Video className="w-12 h-12 text-slate-700 opacity-50" />
                    </div>
                    {/* Simulated OSD */}
                    <div className="absolute bottom-4 right-4 text-xs font-mono text-emerald-500/80">
                      REC • {new Date().toLocaleTimeString()}
                    </div>
                  </>
                ) : (
                  <div className="flex flex-col items-center gap-2 text-slate-600">
                    {camera.status === "error" ? (
                      <WifiOff className="w-12 h-12" />
                    ) : (
                      <VideoOff className="w-12 h-12" />
                    )}
                    <span className="text-sm font-mono uppercase tracking-widest opacity-60">
                      Signal Lost
                    </span>
                  </div>
                )}
              </div>

              {/* Hover Actions */}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4 z-20">
                <button className="p-3 bg-slate-900/90 rounded-full text-white hover:bg-primary-500 transition-colors border border-slate-700">
                  <Maximize2 className="w-5 h-5" />
                </button>
                <button className="p-3 bg-slate-900/90 rounded-full text-white hover:bg-slate-700 transition-colors border border-slate-700">
                  <Settings className="w-5 h-5" />
                </button>
              </div>
            </div>
          );
        })}

        {/* Empty Slots */}
        {Array.from({ length: emptySlots }).map((_, i) => (
          <div
            key={`empty-${i}`}
            className="aspect-video bg-slate-900/50 rounded-xl border-2 border-dashed border-slate-800 flex flex-col items-center justify-center gap-3"
          >
            <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center">
              <Grid className="w-6 h-6 text-slate-600" />
            </div>
            <span className="text-slate-600 text-sm font-medium">
              No Feed Signal
            </span>
          </div>
        ))}
      </div>

      {/* Selected Camera Detail View */}
      {selectedCamera && (
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 animate-in slide-in-from-top-4">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-primary-400" />
            Channel Configuration
          </h3>
          {(() => {
            const cam = results.cameras.find((c) => c.id === selectedCamera);
            if (!cam) return null;
            return (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
                <div>
                  <span className="text-slate-500 block mb-1">Hardware ID</span>
                  <code className="bg-slate-900 px-2 py-1 rounded text-slate-300 font-mono">
                    {cam.id}
                  </code>
                </div>
                <div>
                  <span className="text-slate-500 block mb-1">Network IP</span>
                  <code className="bg-slate-900 px-2 py-1 rounded text-slate-300 font-mono">
                    {cam.ip}
                  </code>
                </div>
                <div>
                  <span className="text-slate-500 block mb-1">
                    Stream Protocol
                  </span>
                  <span className="text-white">RTSP / H.264</span>
                </div>
                <div>
                  <span className="text-slate-500 block mb-1">Resolution</span>
                  <span className="text-white">1920 x 1080 @ 30fps</span>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

// Mock Data Generator
export const generateMockMultiCameraResults = (
  count: number = 3
): MultiCameraResultsType => {
  return {
    type: "multi_camera",
    total_cameras: count,
    active_cameras: count > 2 ? count - 1 : count,
    inactive_cameras: count > 2 ? 1 : 0,
    system_status: "active",
    cameras: Array.from({ length: count }, (_, i) => ({
      id: `CAM-${100 + i}`,
      ip: `192.168.1.${50 + i}`,
      location:
        ["North Gate", "Lobby Main", "Server Room", "Parking East"][i] ||
        `Zone ${i + 1}`,
      description: "Main surveillance feed",
      status: (i === 2 ? "error" : "active") as any,
      stream_url: `rtsp://camera-${i}:554/live`,
      last_frame_at: new Date().toISOString(),
    })),
  };
};
